import type { FpsSample, SegmentFpsSnapshot, VideoFpsSnapshot } from 'src/utils/perf/types';

const DEFAULT_CAPACITY = 60;

const percentileFromSorted = (sorted: number[], p: number): number =>
  sorted[Math.min(Math.floor(sorted.length * p), sorted.length - 1)] ?? 0;

/**
 * FpsSample 시계열을 들고 있다가 percentile 스냅샷을 만들어 주는 데이터 구조.
 *
 * 용도가 다른 두 곳에서 동일 패턴이 반복되어 추출:
 *   1) PerfWindow의 page/source/encode raw fps 배열 4개
 *   2) VideoFpsCollector의 1Hz 합산 sample 배열
 *
 * capacity 도달 시 가장 오래된 sample을 trim한다 (현재는 splice; 큰 capacity는
 * 아직 필요 없어 ring buffer 전환은 보류).
 */
export class FpsSampleBuffer {
  private samples: FpsSample[] = [];

  constructor(private readonly capacity: number = DEFAULT_CAPACITY) {}

  public push(fps: number, timestamp: number = Date.now()): void {
    this.samples.push({ fps, timestamp });
    if (this.samples.length > this.capacity) {
      this.samples.splice(0, this.samples.length - this.capacity);
    }
  }

  public isEmpty(): boolean {
    return this.samples.length === 0;
  }

  /**
   * 전체 버퍼에 대한 percentile 스냅샷.
   * @param withSamples raw FpsSample 배열을 함께 반환할지 (Debug UI sparkline용)
   */
  public getSnapshot(withSamples: boolean): VideoFpsSnapshot | null {
    if (this.samples.length === 0) return null;
    const sorted = this.samples.map((s) => s.fps).sort((a, b) => a - b);
    return {
      current: this.samples[this.samples.length - 1].fps,
      p50: percentileFromSorted(sorted, 0.5),
      p95: percentileFromSorted(sorted, 0.95),
      p05: percentileFromSorted(sorted, 0.05),
      samples: withSamples ? this.samples.slice() : [],
    };
  }

  /**
   * 시간 윈도우 [fromTimestamp, toTimestamp)에 속하는 sample들로만 percentile 계산.
   * 이벤트 리포터의 segment payload가 사용한다.
   */
  public getSegmentSnapshot(fromTimestamp: number, toTimestamp: number): SegmentFpsSnapshot | null {
    const sliced = this.samples.filter(
      (sample) => sample.timestamp >= fromTimestamp && sample.timestamp < toTimestamp
    );
    if (sliced.length === 0) return null;
    const sorted = sliced.map((s) => s.fps).sort((a, b) => a - b);
    return {
      p50: percentileFromSorted(sorted, 0.5),
      p95: percentileFromSorted(sorted, 0.95),
      p05: percentileFromSorted(sorted, 0.05),
    };
  }
}
